import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { NetworkMap } from '@blisspointmedia/bpm-types/dist/LogsTracker';
import { useSetError } from '@src/redux/modals';
import { downloadJSONToCSV } from '@src/utils/download-utils';
import { awaitJSON, LinearBuyingLambdaFetch } from '@src/utils/fetch-utils';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import {
  LOG_TYPE_LABELS,
  LogsTrackerRow,
  LogTypeType,
  NetworkEdit,
  NetworkEdits
} from './logs-tracker-constants';
import { addEdit, applyEdits, buildNetworkMap, getDefaultWeek } from './logs-tracker-utils';

export interface LogsLaneState {
  type: LogTypeType;
  week: string;
  setWeek: (week: string) => void;
  networkMap: NetworkMap | undefined;
  isLoading: boolean;
  edits: NetworkEdits;
  hasEdits: boolean;
  editNetwork: (network: string, edit: NetworkEdit) => void;
  saveEdits: () => Promise<void>;
  discardEdits: () => void;
  isSaving: boolean;
  download: () => void;
}

export const useLogsLane = (type: LogTypeType): LogsLaneState => {
  const setError = useSetError();
  const [week, setWeek] = useState(() => getDefaultWeek(type));
  const [edits, setEdits] = useState<NetworkEdits>({});
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: rows,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['logsTracker', type, week],
    queryFn: async (): Promise<LogsTrackerRow[]> =>
      awaitJSON<LogsTrackerRow[]>(
        await LinearBuyingLambdaFetch('/logs_tracker', { params: { week, type } })
      ),
    // Several people edit the same week, so refetch on mount and week change instead of serving
    // the app-wide 5-minute cache (which also disables refetchOnMount).
    staleTime: 0,
    refetchOnMount: true,
    // The effect below shows this query's errors; skip the global handler's duplicate modal.
    meta: { suppressGlobalError: true }
  });

  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to fetch ${LOG_TYPE_LABELS[type]}: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError, type]);

  const saveEdits = async () => {
    setIsSaving(true);
    try {
      await LinearBuyingLambdaFetch('/update_logs_tracker', {
        method: 'POST',
        body: { edits, week, type }
      });
      await refetch();
      setEdits({});
      Mixpanel.track(MxE.RecordSaved, {
        page: PAGE_NAMES.LOGS_TRACKER,
        entity: LOG_TYPE_LABELS[type]
      });
    } catch (e) {
      setError({
        message: `Failed to save ${LOG_TYPE_LABELS[type]}: ${e.message}`,
        reportError: e
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    type,
    week,
    setWeek,
    networkMap: rows && applyEdits(buildNetworkMap(rows), edits),
    isLoading,
    edits,
    hasEdits: Object.keys(edits).length > 0,
    editNetwork: (network, edit) => setEdits(current => addEdit(current, network, edit)),
    saveEdits,
    discardEdits: () => setEdits({}),
    isSaving,
    download: () => downloadJSONToCSV(rows ?? [], `${LOG_TYPE_LABELS[type]} Tracker - ${week}`)
  };
};
